import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { getDoctorById, getPublicDoctors } from "../api/axios";
import { DoctorCardHome, normaliseDoctor } from "../components/DoctorCard";
import { galleryImageUrl } from "../api/imageUrl";
import { parseDoctorId } from "../utils/slug";

// Compact label/value row — renders nothing when the value is empty.
const DetailItem = ({ label, value, href, full = false }) => {
  const rendered = Array.isArray(value) ? value.join(", ") : value;
  if (rendered === null || rendered === undefined || rendered === "") return null;
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-semibold mb-0.5">
        {label}
      </p>
      {href ? (
        <a
          href={href}
          target={href.startsWith("http") ? "_blank" : undefined}
          rel="noreferrer"
          className="text-[#00342b] font-semibold hover:underline break-words text-sm md:text-[15px]"
        >
          {rendered}
        </a>
      ) : (
        <p className="text-gray-800 font-medium break-words text-sm md:text-[15px] whitespace-pre-line leading-relaxed">
          {rendered}
        </p>
      )}
    </div>
  );
};

const Group = ({ title, children }) => {
  // Hide the whole group if all its children rendered null.
  const hasContent = (Array.isArray(children) ? children : [children]).some(Boolean);
  if (!hasContent) return null;
  return (
    <div>
      <h4 className="text-sm font-bold uppercase tracking-[0.15em] text-white bg-[#00342b] px-4 py-2.5 rounded-md mb-4 shadow-sm">
        {title}
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 px-1">{children}</div>
    </div>
  );
};

const StatTile = ({ label, value, highlight = false }) => (
  <div
    className={`rounded-2xl border p-4 ${
      highlight ? "bg-emerald-50 border-emerald-100" : "bg-gray-50 border-gray-100"
    }`}
  >
    <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">{label}</p>
    <p className="font-semibold text-gray-800 leading-snug">{value}</p>
  </div>
);

const emptyText = (value, fallback = "Not provided") => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

export default function DoctorProfilePage() {
  const { slug } = useParams();
  const id = parseDoctorId(slug);
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setFetchError("");
      try {
        const [docRes, allRes] = await Promise.all([getDoctorById(id), getPublicDoctors()]);
        if (!mounted) return;
        if (docRes?.success && docRes.data) setDoctor(docRes.data);
        else {
          setDoctor(null);
          setFetchError(docRes?.message || "Doctor not found.");
        }
        if (allRes?.success && Array.isArray(allRes.data)) setOthers(allRes.data);
        else setOthers([]);
      } catch (e) {
        if (!mounted) return;
        console.error(e);
        setDoctor(null);
        setFetchError("Unable to load doctor profile.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  const d = useMemo(() => (doctor ? normaliseDoctor(doctor) : null), [doctor]);

  const gallery = useMemo(() => {
    if (!doctor) return [];
    if (Array.isArray(doctor.gallery)) return doctor.gallery;
    if (Array.isArray(d?.gallery)) return d.gallery;
    return [];
  }, [doctor, d]);

  const degreeSummary = useMemo(() => d?.degree || d?.graduation_degree || "", [d]);
  const yearGraduated = useMemo(() => d?.year_graduated || d?.graduation_year || "", [d]);
  const titleLine = useMemo(() => [d?.practice, d?.category].filter(Boolean).join(" — "), [d]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f3f4f6]">
        <div className="text-center">
          <svg className="w-10 h-10 animate-spin mx-auto mb-3 text-[#00342b]" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <p className="text-gray-500">Loading doctor profile...</p>
        </div>
      </div>
    );
  }

  if (!d) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f3f4f6] px-6">
        <div className="text-center max-w-md">
          <p className="text-gray-500 text-lg mb-2">{fetchError || "Doctor not found."}</p>
          <p className="text-gray-400 text-sm mb-6">
            The record may be missing, unpublished, or no longer available.
          </p>
          <button onClick={() => navigate("/search")} className="bg-[#00342b] text-white px-6 py-3 rounded-xl font-semibold">
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  const languages = Array.isArray(d.languages)
    ? d.languages
    : String(d.languages || "").split(",").map((l) => l.trim()).filter(Boolean);

  const acceptingText = d.acceptingPatients ? "Currently accepting new patients" : "Not accepting new patients";
  const website = doctor?.website_address || d.website_address || "";
  const websiteHref = website ? (website.startsWith("http") ? website : `https://${website}`) : "";

  return (
    <div className="bg-[#f3f4f6] text-gray-900 min-h-screen" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 20% 20%, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.06) 25%, transparent 55%), linear-gradient(135deg, #f6f8f7 0%, #eef0f2 100%)",
          }}
        />
        <div className="relative max-w-[1500px] mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-16 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="relative mx-auto lg:mx-0 max-w-[520px]">
                <div className="absolute -inset-6 md:-inset-10 rounded-[2.5rem] bg-emerald-200/30 blur-3xl" />
                <div className="absolute inset-0 rounded-[2rem] overflow-hidden opacity-35 scale-105 blur-xl">
                  {d.image ? (
                    <img src={d.image} alt={d.name} className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full bg-gray-200" />
                  )}
                </div>
                <div className="relative rounded-[2rem] overflow-hidden shadow-[0_25px_80px_-24px_rgba(0,0,0,0.35)] border border-white/70 bg-white">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/12 via-transparent to-transparent z-10" />
                  <div className="aspect-[4/5] w-full">
                    {d.image ? (
                      <img src={d.image} alt={d.name} className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">
                        <svg className="w-32 h-32" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                <div className="hidden md:flex absolute -bottom-6 left-6 right-6 gap-4">
                  <StatTile label="Status" value={d.acceptingPatients ? "Accepting Patients" : "Closed"} highlight />
                  <StatTile label="Specialty" value={emptyText(d.specialty, "—")} />
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-emerald-100 shadow-sm text-sm font-semibold text-emerald-700">
                {d.dataTag || "Verified Physician"}
              </div>
              <h1 className="mt-5 text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.03]">
                {d.name}
              </h1>
              {d.title ? (
                <h2 className="mt-4 text-2xl sm:text-3xl text-gray-600 font-light italic">{d.title}</h2>
              ) : null}
              <p className="mt-6 text-lg text-gray-700 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                {emptyText(titleLine, "Profile details not provided")}
              </p>
              {d.subspecialty ? (
                <div className="mt-4 inline-flex items-center rounded-full bg-white border border-gray-200 px-4 py-2 text-sm text-gray-700 shadow-sm">
                  Subspecialty: {d.subspecialty}
                </div>
              ) : null}
              {languages.length > 0 ? (
                <div className="mt-8 flex flex-wrap gap-3 justify-center lg:justify-start">
                  {languages.map((lang) => (
                    <span key={lang} className="bg-emerald-50 text-emerald-700 text-sm font-medium px-4 py-2 rounded-full border border-emerald-100">
                      {lang}
                    </span>
                  ))}
                </div>
              ) : null}
              <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-2">
                <div className={`w-3.5 h-3.5 rounded-full ${d.acceptingPatients ? "bg-green-500" : "bg-red-400"}`} />
                <span className="text-sm md:text-base text-gray-600">{acceptingText}</span>
              </div>
              <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4 justify-center lg:justify-start">
                {d.phone ? (
                  <a
                    href={`tel:${d.phone}`}
                    className="inline-flex items-center justify-center gap-2 text-[#064e3b] font-bold text-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                    </svg>
                    {d.phone}
                  </a>
                ) : null}
                {websiteHref ? (
                  <a
                    href={websiteHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 border-2 border-[#064e3b] text-[#064e3b] font-bold py-4 px-8 rounded-2xl hover:bg-[#064e3b]/5 transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z M3.6 9h16.8 M3.6 15h16.8 M12 3a15 15 0 010 18 15 15 0 010-18z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                    </svg>
                    Visit Website
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-[1500px] mx-auto px-6 md:px-12 -mt-6 md:-mt-12 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Complete Profile card */}
          <aside className="lg:col-span-5">
            <div className="bg-white rounded-[2rem] shadow-[0_20px_70px_-20px_rgba(0,0,0,0.12)] p-6 md:p-8 border border-gray-100">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Complete Profile</h3>
                <p className="text-sm text-gray-500 mt-1">Everything patients need to know</p>
              </div>

              <div className="space-y-7">
                <Group title="Credentials">
                  <DetailItem label="Title" value={d.title} />
                  <DetailItem label="Board Certification" value={doctor?.board_certification} />
                  <DetailItem label="Specialty" value={d.specialty} />
                  <DetailItem label="Subspecialty" value={d.subspecialty} />
                  <DetailItem label="Degree" value={degreeSummary} />
                  <DetailItem label="Year Graduated" value={yearGraduated} />
                  <DetailItem label="Experience" value={d.experience ? `${d.experience} years` : ""} />
                  <DetailItem label="Academic Title" value={d.academic_title} />
                  <DetailItem label="Academic Affiliation" value={d.academic_affiliation} />
                  <DetailItem label="Medical School" value={d.medical_school_attended || d.medical_school_affiliation} full />
                </Group>

                <Group title="Practice">
                  <DetailItem label="Practice" value={d.practice} />
                  <DetailItem label="Website" value={website} href={websiteHref || undefined} />
                  <DetailItem label="Location" value={d.location} />
                  <DetailItem label="Address" value={d.address} full />
                  <DetailItem label="Hospital Affiliations" value={d.hospital_affiliations} full />
                </Group>

                <Group title="Contact">
                  <DetailItem label="Phone" value={d.phone} href={d.phone ? `tel:${d.phone}` : undefined} />
                  <DetailItem label="Email" value={d.email} href={d.email ? `mailto:${d.email}` : undefined} />
                  <DetailItem label="Languages" value={languages} />
                  <DetailItem label="Gender" value={d.gender} />
                </Group>

                <Group title="Recognition">
                  <DetailItem label="Awards" value={d.awards} full />
                </Group>
              </div>

              <div className="mt-6 md:hidden grid grid-cols-1 sm:grid-cols-2 gap-3">
                <StatTile label="Status" value={d.acceptingPatients ? "Accepting Patients" : "Closed"} highlight />
                <StatTile label="Specialty" value={emptyText(d.specialty, "—")} />
              </div>
            </div>
          </aside>

          {/* About + gallery */}
          <section className="lg:col-span-7 py-4 lg:py-10 space-y-8">
            <div className="bg-white rounded-[2rem] shadow-[0_20px_70px_-20px_rgba(0,0,0,0.12)] p-6 md:p-10 border border-gray-100">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">About {d.name}</h2>
              <div className="space-y-5 text-base md:text-lg text-gray-600 leading-relaxed">
                {d.bio ? (
                  <p className="whitespace-pre-line">{d.bio}</p>
                ) : (
                  <p>
                    {d.name} is a dedicated physician committed to providing compassionate, culturally
                    sensitive care.
                  </p>
                )}
              </div>
            </div>

            {gallery.length > 0 ? (
              <div className="bg-white rounded-[2rem] shadow-[0_20px_70px_-20px_rgba(0,0,0,0.12)] p-6 md:p-10 border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Gallery</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {gallery.map((g, i) => {
                    const src = galleryImageUrl(g.image || g);
                    return src ? (
                      <div key={g.id || i} className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                        <img src={src} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </main>

      {/* Featured Doctors */}
      <section className="bg-[#f3f4f6] pt-16 md:pt-24 pb-20 px-6 md:px-12 mt-10">
        <div className="max-w-[1500px] mx-auto rounded-[2rem] bg-white p-6 md:p-12 shadow-[0_20px_70px_-20px_rgba(0,0,0,0.08)] border border-gray-100">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
            <h2 className="text-2xl md:text-3xl font-light text-gray-900">
              Featured <span className="font-bold">Muslim</span> Doctors
            </h2>
            <Link to="/search" className="bg-gray-900 text-white px-6 py-3 rounded-xl hover:bg-gray-800 transition w-full sm:w-auto text-center font-medium">
              View All
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
            {others.slice(0, 5).map((doc) => (
              <DoctorCardHome key={doc.id} doctor={doc} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
